import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import { catchMarkerIcon } from '../lib/leafletIcon';
import { listSpots } from '../api/spots';
import { listFishTypes } from '../api/fishTypes';
import { listLureTypes } from '../api/lureTypes';
import { createCatch, uploadCatchPhoto } from '../api/catches';
import type { SpotResponse, FishTypeResponse, LureTypeResponse, CreateCatchRequest } from '../types/api';
import { inputClass } from '../components/ui/inputClass';
import { useAuth } from '../contexts/AuthContext';
import { useOnBehalfTargets } from '../hooks/useOnBehalfTargets';
import { OnBehalfBanner } from '../components/catches/OnBehalfBanner';
import { readPhotoExif } from '../lib/exifPhoto';
import { resolveCaughtAtInput, toLocalInputValue } from '../lib/exifTime';
import { nearestSpot } from '../lib/spotMatch';
import { transcodeToJpeg } from '../lib/imageTranscode';

interface Pin { lat: number; lng: number }

function PinHandler({ onPin }: { onPin: (p: Pin) => void }) {
  useMapEvents({ click(e) { onPin({ lat: e.latlng.lat, lng: e.latlng.lng }); } });
  return null;
}

// MapContainer's `center` prop is creation-only — this recenters the live map when the pin moves
// (e.g. after selecting a spot or seeding from EXIF GPS).
function Recenter({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  useEffect(() => { map.setView([lat, lng]); }, [lat, lng, map]);
  return null;
}

// Browser-local "now" as a datetime-local value (NOT UTC — toISOString would show the wrong wall clock).
const nowInput = () => toLocalInputValue(new Date());

export function LogCatchPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const sessionId: number | undefined =
    typeof (state as { sessionId?: unknown })?.sessionId === 'number'
      ? (state as { sessionId: number }).sessionId : undefined;

  const [spots, setSpots] = useState<SpotResponse[]>([]);
  const [fishTypes, setFishTypes] = useState<FishTypeResponse[]>([]);
  const [spotSelectId, setSpotSelectId] = useState<string>('');
  const [pin, setPin] = useState<Pin | null>(null);
  const [fishTypeId, setFishTypeId] = useState<string>('');
  const [lureTypes, setLureTypes] = useState<LureTypeResponse[]>([]);
  const [lureTypeId, setLureTypeId] = useState<string>('');
  const [lengthInches, setLengthInches] = useState<string>('');
  const [caughtAt, setCaughtAt] = useState<string>(nowInput());
  const [notes, setNotes] = useState<string>('');
  const { user } = useAuth();
  const [isPublic, setIsPublic] = useState<boolean>(user?.defaultCatchPublic ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Photo
  const fileRef = useRef<HTMLInputElement>(null);
  const latestPhotoRef = useRef<File | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [locFromExif, setLocFromExif] = useState<boolean | null>(null); // null = no photo yet
  const [timeFromExif, setTimeFromExif] = useState<boolean>(false);

  const [searchParams] = useSearchParams();
  const [onBehalfOfUserId, setOnBehalfOfUserId] = useState<string>(searchParams.get('onBehalfOf') ?? '');
  const [onBehalfClubId, setOnBehalfClubId] = useState<string>(searchParams.get('clubId') ?? '');
  const { data: targets = [] } = useOnBehalfTargets();
  const showAnglerPicker = targets.length > 0 && sessionId === undefined;

  const selectedTarget = (() => {
    if (!onBehalfOfUserId) return null;
    for (const club of targets) {
      const m = club.members.find(mm => mm.userId === onBehalfOfUserId);
      if (m) return { ...m, clubId: club.clubId, clubName: club.clubName };
    }
    return null;
  })();
  const isOnBehalf = !!selectedTarget;
  const targetClubs = onBehalfOfUserId
    ? targets.filter(c => c.members.some(m => m.userId === onBehalfOfUserId)) : [];
  const showStandaloneClubPicker = isOnBehalf && sessionId === undefined;
  const canSubmit = !showStandaloneClubPicker || onBehalfClubId !== '';

  useEffect(() => {
    listSpots().then(setSpots).catch(() => setError('Failed to load spots.'));
    listFishTypes().then(data => {
      setFishTypes(data);
      if (data.length > 0) setFishTypeId(String(data[0].id));
    }).catch(() => setError('Failed to load fish types.'));
    listLureTypes().then(setLureTypes).catch(() => { /* lure list is optional */ });
  }, []);

  useEffect(() => () => { if (photoPreview) URL.revokeObjectURL(photoPreview); }, [photoPreview]);

  async function handlePhotoPick(file: File) {
    latestPhotoRef.current = file;
    setPhotoFile(file);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(URL.createObjectURL(file));

    // Read EXIF FIRST (before any transcode).
    const exif = await readPhotoExif(file);

    // Guard against a fast successive pick: if a newer file was chosen while this
    // EXIF read was in flight, discard these now-stale results.
    if (latestPhotoRef.current !== file) return;

    if (exif.latitude != null && exif.longitude != null) {
      setPin({ lat: exif.latitude, lng: exif.longitude });
      setLocFromExif(true);
      const match = nearestSpot(spots, exif.latitude, exif.longitude, 200);
      setSpotSelectId(match ? String(match.id) : '');
    } else {
      setLocFromExif(false);
    }

    const t = resolveCaughtAtInput(exif.time);
    if (t) { setCaughtAt(t); setTimeFromExif(true); }
    else { setCaughtAt(nowInput()); setTimeFromExif(false); }
  }

  async function handleSave() {
    if (!pin || !fishTypeId) { setError('Pin a location and choose a fish type.'); return; }
    setSaving(true);
    setError(null);
    try {
      const selectedSpot = spots.find(s => s.id === Number(spotSelectId));
      const req: CreateCatchRequest = {
        spotId: selectedSpot?.id,
        fishTypeId: Number(fishTypeId),
        latitude: pin.lat,
        longitude: pin.lng,
        caughtAt: new Date(caughtAt).toISOString(),
        ...(lengthInches ? { lengthInches: Number(lengthInches) } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(lureTypeId ? { lureTypeId: Number(lureTypeId) } : {}),
        ...(sessionId != null ? { sessionId } : {}),
        ...(isOnBehalf ? { onBehalfOfUserId } : {}),
        ...(isOnBehalf && sessionId == null && onBehalfClubId ? { clubId: Number(onBehalfClubId) } : {}),
        isPublic,
      };

      const created = await createCatch(req);

      // Photo is independent of the saved catch — failure here must not lose the catch.
      if (photoFile) {
        try {
          const jpeg = await transcodeToJpeg(photoFile);
          await uploadCatchPhoto(created.id, jpeg);
        } catch {
          // Catch is already saved; surface a soft note but still navigate.
          setSaving(false);
          navigate(`/catches/${created.id}`, { replace: true, state: { photoFailed: true } });
          return;
        }
      }

      navigate(sessionId != null ? `/sessions/${sessionId}` : `/catches/${created.id}`, { replace: true });
    } catch {
      setError('Failed to save catch. Please try again.');
      setSaving(false);
    }
  }

  const mapCenter: [number, number] = pin
    ? [pin.lat, pin.lng]
    : [spots[0]?.latitude ?? 43.0877, spots[0]?.longitude ?? -89.4227];

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Log a catch</h1>

      {sessionId != null && (
        <div className="rounded-lg bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 px-4 py-2 text-sm text-teal-700 dark:text-teal-300">
          Logging to session
        </div>
      )}
      {error && <p className="text-sm text-red-500">{error}</p>}

      {/* Photo */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm space-y-3">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) void handlePhotoPick(f); }}
        />
        {photoPreview
          ? <img src={photoPreview} alt="Selected catch" className="w-full rounded-lg object-cover max-h-72" />
          : <p className="text-sm text-slate-500 dark:text-slate-400">Add a photo to auto-fill location &amp; time, or enter details manually below.</p>}
        <button
          onClick={() => fileRef.current?.click()}
          className="px-5 py-2 bg-teal-600 dark:bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-700 dark:hover:bg-teal-600 transition-colors cursor-pointer"
        >
          {photoPreview ? 'Change photo' : 'Add photo'}
        </button>

        {locFromExif !== null && (
          <div className="text-xs space-y-1">
            <p>{locFromExif ? '✅' : '⚠️'} Location {locFromExif ? 'read from photo' : 'not in photo — set it on the map'}</p>
            <p>{timeFromExif ? '✅' : '⚠️'} Time {timeFromExif ? 'read from photo' : 'defaulted to now — adjust if needed'}</p>
          </div>
        )}
      </div>

      {/* On-behalf pickers (preserved) */}
      {(showAnglerPicker || showStandaloneClubPicker || (isOnBehalf && selectedTarget)) && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm space-y-4">
          {showAnglerPicker && (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Log for</label>
              <select value={onBehalfOfUserId}
                onChange={(e) => { setOnBehalfOfUserId(e.target.value); setOnBehalfClubId(''); }}
                className={inputClass}>
                <option value="">Myself</option>
                {targets.flatMap(club => club.members.map(m => (
                  <option key={`${club.clubId}-${m.userId}`} value={m.userId}>{club.clubName} — {m.displayName}</option>
                )))}
              </select>
            </div>
          )}
          {showStandaloneClubPicker && (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Club <span className="text-red-500">*</span>
              </label>
              <select value={onBehalfClubId} onChange={(e) => setOnBehalfClubId(e.target.value)} className={inputClass} required>
                <option value="">Select club…</option>
                {targetClubs.map(c => <option key={c.clubId} value={c.clubId}>{c.clubName}</option>)}
              </select>
            </div>
          )}
          {isOnBehalf && selectedTarget && (
            <OnBehalfBanner anglerName={selectedTarget.displayName} clubName={selectedTarget.clubName}
              onClear={() => { setOnBehalfOfUserId(''); setOnBehalfClubId(''); }} />
          )}
        </div>
      )}

      {/* Location — ALWAYS rendered */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm space-y-4">
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50">Location</h2>
        <select
          className={inputClass}
          value={spotSelectId}
          onChange={e => {
            const idStr = e.target.value;
            setSpotSelectId(idStr);
            // Selecting a spot drops the pin at that spot (and recenters the map below).
            // The user can still tap the map to fine-tune the exact catch location.
            const s = spots.find(sp => sp.id === Number(idStr));
            if (s) setPin({ lat: s.latitude, lng: s.longitude });
          }}
        >
          <option value="">— No spot / new spot —</option>
          {spots.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <div className="relative z-0 h-72 w-full rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600">
          <MapContainer center={mapCenter} zoom={13} style={{ height: '100%', width: '100%' }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
            <PinHandler onPin={setPin} />
            {pin && <Recenter lat={pin.lat} lng={pin.lng} />}
            {pin && <Marker position={[pin.lat, pin.lng]} icon={catchMarkerIcon} />}
          </MapContainer>
        </div>
        {pin
          ? <p className="text-xs text-slate-400 dark:text-slate-500">{pin.lat.toFixed(5)}, {pin.lng.toFixed(5)}</p>
          : <p className="text-xs text-amber-600 dark:text-amber-400">Tap the map to mark where you caught the fish.</p>}
      </div>

      {/* Details */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm space-y-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Fish type</label>
          <select className={inputClass} value={fishTypeId} onChange={e => setFishTypeId(e.target.value)}>
            {fishTypes.map(ft => <option key={ft.id} value={ft.id}>{ft.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Lure <span className="text-slate-400 dark:text-slate-500 font-normal">— optional</span>
          </label>
          <select className={inputClass} value={lureTypeId} onChange={e => setLureTypeId(e.target.value)}>
            <option value="">— None —</option>
            {lureTypes.map(lt => <option key={lt.id} value={lt.id}>{lt.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Date &amp; time</label>
          <input type="datetime-local" className={inputClass} value={caughtAt} onChange={e => setCaughtAt(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Length (inches) <span className="text-slate-400 dark:text-slate-500 font-normal">— optional</span>
          </label>
          <input type="number" step="0.1" min="0.01" className={inputClass}
            value={lengthInches} onChange={e => setLengthInches(e.target.value)} placeholder="e.g. 14.5" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Notes <span className="text-slate-400 dark:text-slate-500 font-normal">— optional</span>
          </label>
          <textarea className={`${inputClass} resize-none`} rows={3} value={notes}
            onChange={e => setNotes(e.target.value)} placeholder="Any notes about this catch…" />
        </div>
        <div className="flex items-center justify-between gap-3 pt-1">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              {isPublic ? 'Public catch' : 'Private catch'}
            </label>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {isPublic
                ? 'Visible to others at this spot and in your clubs.'
                : 'Only you (and your club managers) can see it.'}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isPublic}
            onClick={() => setIsPublic((v) => !v)}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer ${
              isPublic ? 'bg-teal-600 dark:bg-teal-500' : 'bg-slate-300 dark:bg-slate-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isPublic ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      <button
        className="px-5 py-2 bg-teal-600 dark:bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-700 dark:hover:bg-teal-600 disabled:opacity-40 transition-colors cursor-pointer"
        disabled={!pin || !fishTypeId || saving || !canSubmit}
        onClick={handleSave}
      >
        {saving ? 'Saving…' : 'Save catch'}
      </button>
      {showStandaloneClubPicker && !onBehalfClubId && (
        <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
          Select a club to log a standalone catch on behalf of another angler.
        </p>
      )}
    </div>
  );
}
